import React from 'react';

import { Group, ScrollArea, Skeleton, Stack, Table } from '@mantine/core';

import { ColumnDef, ColumnId, Density } from '../hooks/useColumnPreferences';
import styles from '../PatientListPage.module.scss';
import TableHeader from '../TableHeader';

interface TableSkeletonProps {
  rows?: number;
  columns?: ColumnDef[];
  density?: Density;
}

/** The v1 header's fixed columns (used when no column config is passed). */
const V1_COLUMN_IDS: ColumnId[] = [
  'status',
  'clinic',
  'lastTransmission',
  'lastCompletedReport',
  'openReport'
];

const ReportSkeleton: React.FC<{ width: number }> = ({ width }) => (
  <Stack gap={4}>
    <Group gap={6} wrap="nowrap">
      <Skeleton height={8} width={8} radius="xl" />
      <Skeleton height={12} width="60%" />
    </Group>
    <Skeleton height={11} width={width} />
    <Skeleton height={11} width="50%" />
  </Stack>
);

const ColumnSkeleton: React.FC<{ id: ColumnId }> = ({ id }) => {
  switch (id) {
    case 'status':
    case 'setup':
      return <Skeleton height={20} width={70} radius="xl" />;
    case 'remoteMonitored':
    case 'diagnosis':
    case 'following':
    case 'demographics':
    case 'insurance':
      return <Skeleton height={12} width="50%" />;
    case 'device':
      return <Skeleton height={12} width="70%" />;
    case 'clinic':
      return <Skeleton height={12} width="70%" />;
    case 'lastTransmission':
      return <Skeleton height={12} width="60%" />;
    case 'lastCompletedReport':
      return <ReportSkeleton width={60} />;
    case 'openReport':
      return <ReportSkeleton width={70} />;
  }
};

const PatientRowSkeleton: React.FC<{ columnIds: ColumnId[] }> = ({ columnIds }) => (
  <Table.Tr>
    {/* Checkbox */}
    <Table.Td>
      <Skeleton height={14} width={14} radius="sm" />
    </Table.Td>

    {/* Patient Cell */}
    <Table.Td>
      <Stack gap={4}>
        <Skeleton height={12} width="65%" />
        <Skeleton height={11} width="80%" />
      </Stack>
    </Table.Td>

    {columnIds.map((id) => (
      <Table.Td key={id}>
        <ColumnSkeleton id={id} />
      </Table.Td>
    ))}

    {/* Options Cell */}
    <Table.Td>
      <Skeleton height={16} width={4} radius="sm" />
    </Table.Td>
  </Table.Tr>
);

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 10, columns, density }) => {
  const columnIds = columns
    ? columns.filter((column) => column.visible).map((column) => column.id)
    : V1_COLUMN_IDS;
  return (
    <ScrollArea
      type="always"
      style={{ flex: 1, minHeight: 0, height: '100%' }}
      styles={{ root: { height: '100%' }, viewport: { height: '100%' } }}>
      <Table>
        <TableHeader isLoading={true} columns={columns} density={density} />
        <Table.Tbody className={styles.tableBody}>
          {Array.from({ length: rows }).map((_, index) => (
            <PatientRowSkeleton key={index} columnIds={columnIds} />
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};

export default TableSkeleton;
